import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clientsApi, creancesApi } from '../services/api';
import {
  Creance,
  CreanceInput,
  CreanceSolde,
  CreancesTotaux,
  Reglement,
  ReglementInput,
  ReleveClient,
} from '../types';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import SearchableSelect from '../components/SearchableSelect';
import { formatDateCourte } from '../utils/desktopVersions';
import { dateAlger, formatHorodatage } from '../utils/ddns';
import {
  classeAnciennete,
  formatMontant,
  libelleAnciennete,
  libellePeriode,
  libelleSolde,
  normaliserMontant,
  sensSolde,
  sommeMontants,
} from '../utils/creances';
import { LOGO_BASE64 } from '../utils/companyAssets';
import './Pages.css';
import './Creances.css';

// Page Créances : suivi en deux étapes. 1) Constater ce que le client doit (date, montant,
// observation) ; 2) enregistrer ce qu'il paie. Le règlement s'impute sur le compte du client, pas
// sur une créance précise : solde = créances − règlements. Le serveur refait tous les contrôles et
// calcule tous les montants (NUMERIC) ; la page ne fait qu'afficher.

type Onglet = 'soldes' | 'constatations' | 'reglements' | 'releve';
type Nature = 'creance' | 'reglement';

interface ClientOption {
  id: number;
  client_name: string;
}

interface Formulaire {
  id_client: number; // 0 : pas encore choisi
  date: string;
  montant: string; // tel que saisi (« 15 000,50 »)
  observation: string;
}

interface ModalState {
  nature: Nature;
  mode: 'create' | 'edit';
  id?: number;
  initial: Formulaire;
  form: Formulaire;
}

const ONGLETS: { id: Onglet; libelle: string }[] = [
  { id: 'soldes', libelle: 'Soldes' },
  { id: 'constatations', libelle: 'Constatations' },
  { id: 'reglements', libelle: 'Règlements' },
  { id: 'releve', libelle: 'Relevé client' },
];

const OBSERVATION_MAX = 1000;
const DATE_MIN = '2000-01-01';
const DATE_MAX = '2099-12-31';

// Coordonnées de l'entreprise, comme l'en-tête du contrat PDF (generateContratPDF)
const ENTREPRISE = {
  raison: 'BOUCHAIR Abdelouahab',
  activite: "Bureau d'études et de consultation en Informatique",
  contact: '11 Rue Rekaif Ali, Sétif · contact@winalco.com · 0661702451',
};

const messageErreur = (err: any, defaut: string): string =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || defaut;

const normaliserObservation = (texte: string): string => texte.replace(/\r\n?/g, '\n').trim();

const memeSaisie = (a: Formulaire, b: Formulaire): boolean =>
  a.id_client === b.id_client &&
  a.date === b.date &&
  normaliserMontant(a.montant) === normaliserMontant(b.montant) &&
  normaliserObservation(a.observation) === normaliserObservation(b.observation);

const dateDe = (nature: Nature, item: Creance | Reglement): string =>
  nature === 'creance' ? (item as Creance).date_creance : (item as Reglement).date_reglement;

const pluriel = (nombre: number, singulier: string, plurielForme = `${singulier}s`): string =>
  `${nombre} ${nombre > 1 ? plurielForme : singulier}`;

const Creances: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast, showConfirm } = useToast();
  const canCreate = hasPermission('creances', 'create');
  const canEdit = hasPermission('creances', 'edit');
  const canDelete = hasPermission('creances', 'delete');

  const [onglet, setOnglet] = useState<Onglet>('soldes');

  // Soldes (et chiffres du haut de page)
  const [soldes, setSoldes] = useState<CreanceSolde[]>([]);
  const [totaux, setTotaux] = useState<CreancesTotaux | null>(null);
  const [loadingSoldes, setLoadingSoldes] = useState(false);
  const [rechercheSolde, setRechercheSolde] = useState('');
  const [masquerSoldes, setMasquerSoldes] = useState(true);

  // Constatations et règlements : filtres communs aux deux onglets
  const [constatations, setConstatations] = useState<Creance[]>([]);
  const [reglements, setReglements] = useState<Reglement[]>([]);
  const [loadingListe, setLoadingListe] = useState(false);
  const [filtreClient, setFiltreClient] = useState(0);
  const [filtreDu, setFiltreDu] = useState('');
  const [filtreAu, setFiltreAu] = useState('');
  const [recherche, setRecherche] = useState('');

  // Relevé d'un client
  const [releveClient, setReleveClient] = useState(0);
  const [releveDu, setReleveDu] = useState('');
  const [releveAu, setReleveAu] = useState('');
  const [releve, setReleve] = useState<ReleveClient | null>(null);
  const [loadingReleve, setLoadingReleve] = useState(false);

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Une réponse arrivée après une plus récente (filtre changé entre-temps) est ignorée
  const sequenceListe = useRef(0);
  const sequenceReleve = useRef(0);
  const clientsDemandes = useRef(false);

  const periodeListeInvalide = filtreDu !== '' && filtreAu !== '' && filtreAu < filtreDu;
  const periodeReleveInvalide = releveDu !== '' && releveAu !== '' && releveAu < releveDu;

  // ---- Chargements -----------------------------------------------------------------------
  const loadSoldes = useCallback(async () => {
    try {
      setLoadingSoldes(true);
      const response = await creancesApi.getSoldes();
      setSoldes(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotaux(response.data?.totaux ?? null);
    } catch (err: any) {
      showToast(messageErreur(err, 'Chargement des soldes impossible'), 'error');
    } finally {
      setLoadingSoldes(false);
    }
  }, [showToast]);

  const loadListe = useCallback(async (quelle: 'constatations' | 'reglements') => {
    const numero = ++sequenceListe.current;
    if (filtreDu !== '' && filtreAu !== '' && filtreAu < filtreDu) {
      setLoadingListe(false);
      return;
    }
    const filtres = { client_id: filtreClient, du: filtreDu, au: filtreAu };
    try {
      setLoadingListe(true);
      const response = quelle === 'constatations'
        ? await creancesApi.getConstatations(filtres)
        : await creancesApi.getReglements(filtres);
      if (numero !== sequenceListe.current) {
        return;
      }
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      if (quelle === 'constatations') {
        setConstatations(items);
      } else {
        setReglements(items);
      }
    } catch (err: any) {
      if (numero === sequenceListe.current) {
        showToast(messageErreur(err, 'Chargement impossible'), 'error');
      }
    } finally {
      if (numero === sequenceListe.current) {
        setLoadingListe(false);
      }
    }
  }, [filtreClient, filtreDu, filtreAu, showToast]);

  const loadReleve = useCallback(async () => {
    const numero = ++sequenceReleve.current;
    if (!releveClient || (releveDu !== '' && releveAu !== '' && releveAu < releveDu)) {
      setReleve(null);
      setLoadingReleve(false);
      return;
    }
    try {
      setLoadingReleve(true);
      const response = await creancesApi.getReleve(releveClient, { du: releveDu, au: releveAu });
      if (numero === sequenceReleve.current) {
        setReleve(Array.isArray(response.data?.lignes) ? response.data : null);
      }
    } catch (err: any) {
      if (numero === sequenceReleve.current) {
        setReleve(null);
        showToast(messageErreur(err, 'Chargement du relevé impossible'), 'error');
      }
    } finally {
      if (numero === sequenceReleve.current) {
        setLoadingReleve(false);
      }
    }
  }, [releveClient, releveDu, releveAu, showToast]);

  // La liste complète des clients, pour choisir le client d'une nouvelle opération ou d'un
  // relevé. À défaut (droit clients:view absent), les clients qui ont déjà un compte.
  const loadClients = useCallback(async () => {
    if (clientsDemandes.current) {
      return;
    }
    clientsDemandes.current = true;
    try {
      const response = await clientsApi.getAll();
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.clients || [];
      setClients(data.map((client: any) => ({ id: Number(client.id), client_name: String(client.client_name ?? '') })));
    } catch (err: any) {
      clientsDemandes.current = false;
      showToast(messageErreur(err, 'Chargement de la liste des clients impossible'), 'error');
    }
  }, [showToast]);

  useEffect(() => {
    loadSoldes();
  }, [loadSoldes]);

  useEffect(() => {
    if (onglet === 'constatations' || onglet === 'reglements') {
      loadListe(onglet);
    }
  }, [onglet, loadListe]);

  useEffect(() => {
    if (onglet === 'releve') {
      loadReleve();
    }
  }, [onglet, loadReleve]);

  useEffect(() => {
    if (onglet !== 'soldes') {
      loadClients();
    }
  }, [onglet, loadClients]);

  // Impression du relevé : la feuille de style n'imprime que le relevé tant que l'onglet est ouvert
  useEffect(() => {
    if (onglet !== 'releve') {
      return undefined;
    }
    document.body.classList.add('cr-impression-releve');
    return () => document.body.classList.remove('cr-impression-releve');
  }, [onglet]);

  const rafraichir = useCallback(async () => {
    await Promise.all([
      loadSoldes(),
      onglet === 'constatations' || onglet === 'reglements' ? loadListe(onglet) : Promise.resolve(),
      onglet === 'releve' ? loadReleve() : Promise.resolve(),
    ]);
  }, [loadSoldes, loadListe, loadReleve, onglet]);

  // ---- Données dérivées ------------------------------------------------------------------
  const optionsClients = useMemo(() => {
    const noms = new Map<number, string>();
    soldes.forEach((solde) => noms.set(solde.id_client, solde.client_name));
    clients.forEach((client) => noms.set(client.id, client.client_name));
    return Array.from(noms, ([value, label]) => ({ value, label: label || `Client ${value}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [clients, soldes]);

  const soldeParClient = useMemo(
    () => new Map(soldes.map((solde) => [solde.id_client, solde.solde] as [number, number])),
    [soldes]
  );

  const stats = useMemo(() => {
    const debiteurs = soldes.filter((solde) => sensSolde(solde.solde) === 'debiteur');
    const crediteurs = soldes.filter((solde) => sensSolde(solde.solde) === 'crediteur');
    return {
      debiteurs: debiteurs.length,
      crediteurs: crediteurs.length,
      soldes: soldes.length - debiteurs.length - crediteurs.length,
      aRecouvrer: sommeMontants(debiteurs.map((solde) => solde.solde)),
      avances: sommeMontants(crediteurs.map((solde) => -solde.solde)),
    };
  }, [soldes]);

  const soldesAffiches = useMemo(() => {
    const texte = rechercheSolde.trim().toLowerCase();
    return soldes.filter((solde) =>
      (!masquerSoldes || sensSolde(solde.solde) !== 'solde') &&
      (!texte || solde.client_name.toLowerCase().includes(texte)));
  }, [soldes, rechercheSolde, masquerSoldes]);

  const constatationsAffichees = useMemo(() => {
    const texte = recherche.trim().toLowerCase();
    if (!texte) {
      return constatations;
    }
    return constatations.filter((creance) =>
      creance.client_name.toLowerCase().includes(texte) ||
      (creance.observation || '').toLowerCase().includes(texte));
  }, [constatations, recherche]);

  const reglementsAffiches = useMemo(() => {
    const texte = recherche.trim().toLowerCase();
    return texte ? reglements.filter((reglement) => reglement.client_name.toLowerCase().includes(texte)) : reglements;
  }, [reglements, recherche]);

  // ---- Saisie : mêmes règles que le serveur ----------------------------------------------
  const formCheck = useMemo(() => {
    if (!modal) {
      return { valid: false, client: '', date: '', montant: '', observation: '', futur: false, montantNormalise: null as string | null };
    }
    const { form } = modal;
    const client = form.id_client ? '' : 'Choisir le client';
    let date = '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      date = 'Date obligatoire';
    } else if (form.date < DATE_MIN || form.date > DATE_MAX) {
      date = 'Date entre 2000 et 2099';
    }
    const montantNormalise = normaliserMontant(form.montant);
    let montant = '';
    if (form.montant.trim() === '') {
      montant = 'Montant obligatoire';
    } else if (!montantNormalise) {
      montant = 'Montant invalide : nombre positif, 2 décimales au plus (ex. 15 000 ou 15 000,50)';
    }
    const observation = form.observation.length > OBSERVATION_MAX
      ? `${OBSERVATION_MAX.toLocaleString('fr-FR')} caractères au plus`
      : '';
    const inchange = modal.mode === 'edit' && memeSaisie(modal.initial, form);
    return {
      valid: !client && !date && !montant && !observation && !inchange,
      client,
      date,
      montant,
      observation,
      futur: !date && form.date > dateAlger(),
      montantNormalise,
    };
  }, [modal]);

  // Effet sur le solde du client choisi. En correction, l'opération corrigée est déjà dans le
  // solde : on l'en retire d'abord.
  const impact = useMemo(() => {
    if (!modal || !modal.form.id_client) {
      return null;
    }
    const signe = modal.nature === 'creance' ? 1 : -1;
    let avant = soldeParClient.get(modal.form.id_client) ?? 0;
    if (modal.mode === 'edit' && modal.initial.id_client === modal.form.id_client) {
      avant = sommeMontants([avant, -signe * Number(normaliserMontant(modal.initial.montant) ?? 0)]);
    }
    const apres = formCheck.montantNormalise === null
      ? null
      : sommeMontants([avant, signe * Number(formCheck.montantNormalise)]);
    return { avant, apres };
  }, [modal, formCheck.montantNormalise, soldeParClient]);

  const updateForm = (patch: Partial<Formulaire>) =>
    setModal((courant) => (courant ? { ...courant, form: { ...courant.form, ...patch } } : courant));

  const ouvrirSaisie = (nature: Nature, clientId = 0) => {
    const form: Formulaire = { id_client: clientId, date: dateAlger(), montant: '', observation: '' };
    setModal({ nature, mode: 'create', initial: form, form });
    loadClients();
  };

  const ouvrirCorrection = (nature: Nature, item: Creance | Reglement) => {
    const form: Formulaire = {
      id_client: item.id_client,
      date: dateDe(nature, item),
      montant: formatMontant(item.montant, false),
      observation: nature === 'creance' ? (item as Creance).observation || '' : '',
    };
    setModal({ nature, mode: 'edit', id: item.id, initial: form, form });
    loadClients();
  };

  const enregistrer = async (etat: ModalState) => {
    const montant = normaliserMontant(etat.form.montant);
    if (!montant) {
      return;
    }
    const creance = etat.nature === 'creance';
    try {
      setSaving(true);
      if (creance) {
        const payload: CreanceInput = {
          id_client: etat.form.id_client,
          date_creance: etat.form.date,
          montant,
          observation: normaliserObservation(etat.form.observation) || null,
        };
        if (etat.mode === 'create') {
          await creancesApi.createConstatation(payload);
        } else {
          await creancesApi.updateConstatation(etat.id as number, payload);
        }
      } else {
        const payload: ReglementInput = {
          id_client: etat.form.id_client,
          date_reglement: etat.form.date,
          montant,
        };
        if (etat.mode === 'create') {
          await creancesApi.createReglement(payload);
        } else {
          await creancesApi.updateReglement(etat.id as number, payload);
        }
      }
      setModal(null);
      const somme = formatMontant(Number(montant));
      if (etat.mode === 'edit') {
        showToast(creance ? 'Créance modifiée' : 'Règlement modifié', 'success');
      } else {
        showToast(creance ? `Créance de ${somme} constatée` : `Règlement de ${somme} enregistré`, 'success');
      }
      await rafraichir();
    } catch (err: any) {
      showToast(messageErreur(err, 'Enregistrement impossible'), 'error');
      // Ligne supprimée depuis un autre poste : la liste affichée n'est plus à jour
      if (err?.response?.status === 404) {
        setModal(null);
        await rafraichir();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!modal || !formCheck.valid) {
      return;
    }
    const etat = modal;
    if (etat.nature === 'reglement' && impact && impact.apres !== null && sensSolde(impact.apres) === 'crediteur') {
      showConfirm(
        `Ce règlement dépasse ce que doit le client (${formatMontant(Math.max(impact.avant, 0))}). ` +
          `Après enregistrement, il sera créditeur de ${formatMontant(-impact.apres)} (avance). ` +
          'Enregistrer quand même ?',
        () => { void enregistrer(etat); },
        'Règlement supérieur au solde'
      );
      return;
    }
    void enregistrer(etat);
  };

  const supprimer = (nature: Nature, item: Creance | Reglement) => {
    const creance = nature === 'creance';
    showConfirm(
      `Supprimer ${creance ? 'la créance' : 'le règlement'} de ${formatMontant(item.montant)} du ` +
        `${formatDateCourte(dateDe(nature, item))} (${item.client_name}) ? Le solde du client sera ` +
        'recalculé ; la suppression reste tracée dans l’historique.',
      async () => {
        try {
          setBusyKey(`${nature}-${item.id}`);
          if (creance) {
            await creancesApi.removeConstatation(item.id);
          } else {
            await creancesApi.removeReglement(item.id);
          }
          showToast(creance ? 'Créance supprimée' : 'Règlement supprimé', 'success');
        } catch (err: any) {
          showToast(messageErreur(err, 'Suppression impossible'), 'error');
        } finally {
          setBusyKey(null);
          await rafraichir();
        }
      },
      creance ? 'Supprimer la créance' : 'Supprimer le règlement'
    );
  };

  const voirReleve = (clientId: number) => {
    setReleveClient(clientId);
    setOnglet('releve');
  };

  const effacerFiltres = () => {
    setFiltreClient(0);
    setFiltreDu('');
    setFiltreAu('');
    setRecherche('');
  };

  const anneeEnCours = () => {
    const aujourdhui = dateAlger();
    setReleveDu(`${aujourdhui.slice(0, 4)}-01-01`);
    setReleveAu(aujourdhui);
  };

  // Le titre de la page devient le nom proposé pour « Enregistrer en PDF »
  const imprimer = () => {
    if (!releve) {
      return;
    }
    const titre = document.title;
    document.title = `Relevé ${releve.client.client_name} ${dateAlger()}`;
    const retablir = () => {
      document.title = titre;
      window.removeEventListener('afterprint', retablir);
    };
    window.addEventListener('afterprint', retablir);
    window.print();
  };

  // ---- Rendu -----------------------------------------------------------------------------
  const boutonsLigne = (nature: Nature, item: Creance | Reglement) => {
    const occupe = busyKey === `${nature}-${item.id}`;
    const quoi = nature === 'creance' ? 'la créance' : 'le règlement';
    const libelle = `${quoi} du ${formatDateCourte(dateDe(nature, item))} (${item.client_name})`;
    return (
      <div className="cr-actions">
        {canEdit && (
          <button className="btn btn-secondary" disabled={occupe} onClick={() => ouvrirCorrection(nature, item)}
            aria-label={`Modifier ${libelle}`}>
            Modifier
          </button>
        )}
        {canDelete && (
          <button className="btn btn-secondary cr-btn-danger" disabled={occupe} onClick={() => supprimer(nature, item)}
            aria-label={`Supprimer ${libelle}`}>
            Supprimer
          </button>
        )}
        {!canEdit && !canDelete && '-'}
      </div>
    );
  };

  const saisie = (item: Creance | Reglement) => (
    <td className="cr-secondaire" title={item.cree_le ? `Saisi le ${formatHorodatage(item.cree_le)}` : undefined}>
      {item.saisi_par || '-'}
      <div>{formatHorodatage(item.cree_le)}</div>
    </td>
  );

  const barreFiltres = (
    <>
      <div className="filters cr-filtres">
        <div className="cr-filtre-client">
          <label htmlFor="cr-filtre-client" className="cr-cache">Client</label>
          <SearchableSelect inputId="cr-filtre-client" options={optionsClients} value={filtreClient}
            onChange={setFiltreClient} placeholder="Tous les clients" />
        </div>
        <label className="cr-filtre-date">
          Du
          <input type="date" value={filtreDu} min={DATE_MIN} max={DATE_MAX} onChange={(event) => setFiltreDu(event.target.value)} />
        </label>
        <label className="cr-filtre-date">
          Au
          <input type="date" value={filtreAu} min={DATE_MIN} max={DATE_MAX} onChange={(event) => setFiltreAu(event.target.value)} />
        </label>
        <input
          type="search"
          className="search-input"
          placeholder={onglet === 'constatations' ? 'Rechercher un client, une observation…' : 'Rechercher un client…'}
          aria-label="Rechercher dans la liste"
          value={recherche}
          onChange={(event) => setRecherche(event.target.value)}
        />
        {(filtreClient !== 0 || filtreDu !== '' || filtreAu !== '' || recherche !== '') && (
          <button type="button" className="btn btn-secondary" onClick={effacerFiltres}>
            Effacer les filtres
          </button>
        )}
      </div>
      {periodeListeInvalide && <p className="cr-erreur">La date de fin précède la date de début.</p>}
    </>
  );

  const titreModal = modal
    ? modal.mode === 'create'
      ? modal.nature === 'creance' ? 'Constater une créance' : 'Enregistrer un règlement'
      : modal.nature === 'creance' ? 'Modifier la créance' : 'Modifier le règlement'
    : '';

  const lieu = releve
    ? Array.from(new Set([releve.client.ville, releve.client.wilaya].filter((valeur): valeur is string => Boolean(valeur))))
      .join(', ')
    : '';

  return (
    <div className="page-container creances">
      <div className="page-header">
        <div>
          <h1>Créances</h1>
          <p className="cr-subtitle">
            Suivi en deux étapes : constater ce que doit le client, puis enregistrer ses règlements.
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => { void rafraichir(); }} disabled={loadingSoldes}>
            Actualiser
          </button>
          {canCreate && (
            <button className="btn btn-primary" onClick={() => ouvrirSaisie('creance')}>
              Constater une créance
            </button>
          )}
          {canCreate && (
            <button className="btn btn-success" onClick={() => ouvrirSaisie('reglement')}>
              Enregistrer un règlement
            </button>
          )}
        </div>
      </div>

      <div className="cr-kpis" aria-live="polite">
        <div className="cr-kpi">
          <span className="cr-kpi-libelle">Total constaté</span>
          <span className="cr-kpi-valeur">{formatMontant(totaux?.total_creances ?? 0)}</span>
        </div>
        <div className="cr-kpi">
          <span className="cr-kpi-libelle">Total réglé</span>
          <span className="cr-kpi-valeur">{formatMontant(totaux?.total_reglements ?? 0)}</span>
        </div>
        <div className="cr-kpi cr-kpi-accent">
          <span className="cr-kpi-libelle">Reste à recouvrer</span>
          <span className="cr-kpi-valeur">{formatMontant(stats.aRecouvrer)}</span>
          <span className="cr-kpi-detail">{pluriel(stats.debiteurs, 'client débiteur', 'clients débiteurs')}</span>
        </div>
        {stats.crediteurs > 0 && (
          <div className="cr-kpi">
            <span className="cr-kpi-libelle">Avances reçues</span>
            <span className="cr-kpi-valeur">{formatMontant(stats.avances)}</span>
            <span className="cr-kpi-detail">{pluriel(stats.crediteurs, 'client créditeur', 'clients créditeurs')}</span>
          </div>
        )}
      </div>

      <details className="cr-aide-bloc">
        <summary>Comment ça marche</summary>
        <ul>
          <li>
            <strong>Étape 1 — Constater une créance</strong> : ce que le client doit (date, montant, observation :
            facture, prestation…).
          </li>
          <li>
            <strong>Étape 2 — Enregistrer un règlement</strong> : ce que le client a payé. Il s&apos;impute sur le
            compte du client, pas sur une créance précise.
          </li>
          <li>
            Solde = créances − règlements. Un solde négatif signifie que le client a payé d&apos;avance.
          </li>
          <li>
            « Plus ancienne impayée » : les règlements couvrent d&apos;abord les créances les plus anciennes ; c&apos;est
            la date de la première créance qu&apos;ils ne couvrent pas entièrement.
          </li>
        </ul>
      </details>

      <div className="cr-onglets" role="tablist" aria-label="Suivi des créances">
        {ONGLETS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`cr-onglet-${item.id}`}
            aria-selected={onglet === item.id}
            aria-controls={`cr-panneau-${item.id}`}
            className={`cr-onglet ${onglet === item.id ? 'cr-onglet-actif' : ''}`}
            onClick={() => setOnglet(item.id)}
          >
            {item.libelle}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`cr-panneau-${onglet}`}
        aria-labelledby={`cr-onglet-${onglet}`}
        className={`cr-panneau cr-panneau-${onglet}`}
      >
        {onglet === 'soldes' && (
          <>
            <div className="filters">
              <input
                type="search"
                className="search-input"
                placeholder="Rechercher un client…"
                aria-label="Rechercher un client"
                value={rechercheSolde}
                onChange={(event) => setRechercheSolde(event.target.value)}
              />
              <label className="cr-case">
                <input type="checkbox" checked={masquerSoldes} onChange={(event) => setMasquerSoldes(event.target.checked)} />
                Masquer les comptes soldés ({stats.soldes})
              </label>
            </div>
            <div className="table-container">
              <table className="cr-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="cr-num">Constaté</th>
                    <th className="cr-num">Réglé</th>
                    <th className="cr-num">Solde</th>
                    <th>Plus ancienne impayée</th>
                    <th>Dernière opération</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingSoldes && soldes.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty">Chargement...</td>
                    </tr>
                  )}
                  {!loadingSoldes && soldesAffiches.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty">
                        {soldes.length === 0
                          ? 'Aucune créance ni aucun règlement. « Constater une créance » pour commencer.'
                          : 'Aucun compte pour cette recherche.'}
                      </td>
                    </tr>
                  )}
                  {soldesAffiches.map((solde) => {
                    const sens = sensSolde(solde.solde);
                    return (
                      <tr key={solde.id_client}>
                        <td>
                          <button type="button" className="cr-lien" onClick={() => voirReleve(solde.id_client)}
                            title="Voir le relevé">
                            {solde.client_name}
                          </button>
                          <div className="cr-secondaire">
                            {pluriel(solde.nb_creances, 'créance')}, {pluriel(solde.nb_reglements, 'règlement')}
                          </div>
                        </td>
                        <td className="cr-num">{formatMontant(solde.total_creances)}</td>
                        <td className="cr-num">{formatMontant(solde.total_reglements)}</td>
                        <td className="cr-num">
                          <span className={`cr-solde cr-solde-${sens}`}>{formatMontant(solde.solde)}</span>
                          {sens !== 'debiteur' && (
                            <div className="cr-secondaire">{sens === 'crediteur' ? 'avance' : 'soldé'}</div>
                          )}
                        </td>
                        <td>
                          {solde.plus_ancienne_impayee ? (
                            <>
                              <div>{formatDateCourte(solde.plus_ancienne_impayee)}</div>
                              <span className={`cr-pastille cr-anciennete-${classeAnciennete(solde.anciennete_jours ?? 0)}`}>
                                {libelleAnciennete(solde.anciennete_jours ?? 0)}
                              </span>
                            </>
                          ) : '-'}
                        </td>
                        <td>{formatDateCourte(solde.derniere_operation)}</td>
                        <td>
                          <div className="cr-actions">
                            <button className="btn btn-secondary" onClick={() => voirReleve(solde.id_client)}
                              aria-label={`Relevé de ${solde.client_name}`}>
                              Relevé
                            </button>
                            {canCreate && sens === 'debiteur' && (
                              <button className="btn btn-success" onClick={() => ouvrirSaisie('reglement', solde.id_client)}
                                aria-label={`Enregistrer un règlement de ${solde.client_name}`}>
                                Règlement
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {soldesAffiches.length > 0 && (
                  <tfoot>
                    <tr>
                      <th>Total ({pluriel(soldesAffiches.length, 'client')})</th>
                      <th className="cr-num">{formatMontant(sommeMontants(soldesAffiches.map((solde) => solde.total_creances)))}</th>
                      <th className="cr-num">{formatMontant(sommeMontants(soldesAffiches.map((solde) => solde.total_reglements)))}</th>
                      <th className="cr-num">{formatMontant(sommeMontants(soldesAffiches.map((solde) => solde.solde)))}</th>
                      <th colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}

        {onglet === 'constatations' && (
          <>
            {barreFiltres}
            <div className="table-container">
              <table className="cr-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th className="cr-num">Montant</th>
                    <th>Observation</th>
                    <th>Saisie</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingListe && constatations.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty">Chargement...</td>
                    </tr>
                  )}
                  {!loadingListe && constatationsAffichees.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty">Aucune créance pour ces critères.</td>
                    </tr>
                  )}
                  {constatationsAffichees.map((creance) => (
                    <tr key={creance.id}>
                      <td>{formatDateCourte(creance.date_creance)}</td>
                      <td>
                        <button type="button" className="cr-lien" onClick={() => voirReleve(creance.id_client)}
                          title="Voir le relevé">
                          {creance.client_name}
                        </button>
                      </td>
                      <td className="cr-num">{formatMontant(creance.montant)}</td>
                      <td className="cr-observation">{creance.observation || '-'}</td>
                      {saisie(creance)}
                      <td>{boutonsLigne('creance', creance)}</td>
                    </tr>
                  ))}
                </tbody>
                {constatationsAffichees.length > 0 && (
                  <tfoot>
                    <tr>
                      <th colSpan={2}>Total ({pluriel(constatationsAffichees.length, 'créance')})</th>
                      <th className="cr-num">{formatMontant(sommeMontants(constatationsAffichees.map((creance) => creance.montant)))}</th>
                      <th colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}

        {onglet === 'reglements' && (
          <>
            {barreFiltres}
            <div className="table-container">
              <table className="cr-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th className="cr-num">Montant</th>
                    <th>Saisie</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingListe && reglements.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty">Chargement...</td>
                    </tr>
                  )}
                  {!loadingListe && reglementsAffiches.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty">Aucun règlement pour ces critères.</td>
                    </tr>
                  )}
                  {reglementsAffiches.map((reglement) => (
                    <tr key={reglement.id}>
                      <td>{formatDateCourte(reglement.date_reglement)}</td>
                      <td>
                        <button type="button" className="cr-lien" onClick={() => voirReleve(reglement.id_client)}
                          title="Voir le relevé">
                          {reglement.client_name}
                        </button>
                      </td>
                      <td className="cr-num">{formatMontant(reglement.montant)}</td>
                      {saisie(reglement)}
                      <td>{boutonsLigne('reglement', reglement)}</td>
                    </tr>
                  ))}
                </tbody>
                {reglementsAffiches.length > 0 && (
                  <tfoot>
                    <tr>
                      <th colSpan={2}>Total ({pluriel(reglementsAffiches.length, 'règlement')})</th>
                      <th className="cr-num">{formatMontant(sommeMontants(reglementsAffiches.map((reglement) => reglement.montant)))}</th>
                      <th colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}

        {onglet === 'releve' && (
          <>
            <div className="cr-releve-filtres cr-sans-impression">
              <div className="form-group cr-releve-client">
                <label htmlFor="cr-releve-client">Client</label>
                <SearchableSelect inputId="cr-releve-client" options={optionsClients} value={releveClient}
                  onChange={setReleveClient} placeholder="Rechercher un client…" />
              </div>
              <div className="form-group">
                <label htmlFor="cr-releve-du">Du</label>
                <input id="cr-releve-du" type="date" value={releveDu} min={DATE_MIN} max={DATE_MAX}
                  onChange={(event) => setReleveDu(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="cr-releve-au">Au</label>
                <input id="cr-releve-au" type="date" value={releveAu} min={DATE_MIN} max={DATE_MAX}
                  onChange={(event) => setReleveAu(event.target.value)} />
              </div>
              <div className="cr-releve-boutons">
                <button type="button" className="btn btn-secondary" onClick={anneeEnCours}>
                  Année en cours
                </button>
                <button type="button" className="btn btn-secondary" disabled={releveDu === '' && releveAu === ''}
                  onClick={() => { setReleveDu(''); setReleveAu(''); }}>
                  Toute la période
                </button>
                <button type="button" className="btn btn-primary" onClick={imprimer} disabled={!releve || loadingReleve}>
                  Imprimer / PDF
                </button>
              </div>
            </div>
            {periodeReleveInvalide && <p className="cr-erreur cr-sans-impression">La date de fin précède la date de début.</p>}
            {!releveClient && (
              <p className="cr-vide cr-sans-impression">Choisir un client pour afficher son relevé de compte.</p>
            )}
            {releveClient > 0 && loadingReleve && !releve && <p className="cr-vide">Chargement...</p>}

            {releve && (
              <section className="cr-releve" aria-labelledby="cr-releve-titre" aria-busy={loadingReleve}>
                <div className="cr-impression-entete">
                  <img src={LOGO_BASE64} alt="" className="cr-impression-logo" />
                  <div>
                    <div className="cr-impression-raison">{ENTREPRISE.raison}</div>
                    <div>{ENTREPRISE.activite}</div>
                    <div>{ENTREPRISE.contact}</div>
                  </div>
                </div>

                <div className="cr-releve-titre-ligne">
                  <h2 id="cr-releve-titre">Relevé de compte client</h2>
                  <span className="cr-secondaire">Édité le {formatDateCourte(dateAlger())}</span>
                </div>

                <div className="cr-releve-identite">
                  <div>
                    <div className="cr-releve-client-nom">{releve.client.client_name}</div>
                    {lieu && <div>{lieu}</div>}
                    {releve.client.telephone && <div>Tél. {releve.client.telephone}</div>}
                    {releve.client.email && <div>{releve.client.email}</div>}
                  </div>
                  <div className="cr-releve-periode">
                    <div>Période : <strong>{libellePeriode(releve.du, releve.au)}</strong></div>
                    <div className={`cr-solde cr-solde-${sensSolde(releve.solde_final)}`}>
                      {releve.au ? `Au ${formatDateCourte(releve.au)} : ` : ''}{libelleSolde(releve.solde_final)}
                    </div>
                  </div>
                </div>

                <div className="table-container">
                  <table className="cr-table cr-releve-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Opération</th>
                        <th>Observation</th>
                        <th className="cr-num">Débit</th>
                        <th className="cr-num">Crédit</th>
                        <th className="cr-num">Solde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {releve.du && (
                        <tr className="cr-releve-report">
                          <td>{formatDateCourte(releve.du)}</td>
                          <td colSpan={4}>Solde antérieur (report)</td>
                          <td className="cr-num">{formatMontant(releve.solde_initial, false)}</td>
                        </tr>
                      )}
                      {releve.lignes.length === 0 && (
                        <tr>
                          <td colSpan={6} className="empty">Aucun mouvement sur la période.</td>
                        </tr>
                      )}
                      {releve.lignes.map((ligne) => (
                        <tr key={`${ligne.nature}-${ligne.id}`}>
                          <td>{formatDateCourte(ligne.date_operation)}</td>
                          <td>{ligne.nature === 'CREANCE' ? 'Créance' : 'Règlement'}</td>
                          <td className="cr-observation">{ligne.observation || ''}</td>
                          <td className="cr-num">{ligne.debit ? formatMontant(ligne.debit, false) : ''}</td>
                          <td className="cr-num">{ligne.credit ? formatMontant(ligne.credit, false) : ''}</td>
                          <td className="cr-num">{formatMontant(ligne.solde, false)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan={3}>Totaux de la période</th>
                        <th className="cr-num">{formatMontant(releve.total_debit, false)}</th>
                        <th className="cr-num">{formatMontant(releve.total_credit, false)}</th>
                        <th />
                      </tr>
                      <tr className="cr-releve-final">
                        <th colSpan={5}>Solde {releve.au ? `au ${formatDateCourte(releve.au)}` : 'à ce jour'}</th>
                        <th className="cr-num">{formatMontant(releve.solde_final)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="cr-releve-legende">
                  Montants en dinars algériens (DA). Solde positif : somme due par le client ; négatif : avance du client.
                </p>
              </section>
            )}
          </>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal cr-modal" role="dialog" aria-modal="true" aria-labelledby="cr-modal-titre">
            <h2 id="cr-modal-titre">{titreModal}</h2>
            <p className="cr-etape">
              {modal.nature === 'creance' ? 'Étape 1 : ce que le client doit.' : 'Étape 2 : ce que le client a payé.'}
            </p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="cr-client">Client</label>
                <SearchableSelect inputId="cr-client" options={optionsClients} value={modal.form.id_client}
                  onChange={(valeur) => updateForm({ id_client: valeur })} placeholder="Rechercher un client…" />
                {formCheck.client && <p className="cr-aide">{formCheck.client}</p>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cr-date">{modal.nature === 'creance' ? 'Date de la créance' : 'Date du règlement'}</label>
                  <input id="cr-date" type="date" value={modal.form.date} min={DATE_MIN} max={DATE_MAX}
                    onChange={(event) => updateForm({ date: event.target.value })}
                    aria-invalid={Boolean(formCheck.date)} />
                </div>
                <div className="form-group">
                  <label htmlFor="cr-montant">Montant (DA)</label>
                  <input id="cr-montant" className="cr-montant-saisie" inputMode="decimal" autoComplete="off"
                    placeholder="ex. 15 000 ou 15 000,50" value={modal.form.montant}
                    onChange={(event) => updateForm({ montant: event.target.value })}
                    aria-invalid={Boolean(formCheck.montant) && modal.form.montant.trim() !== ''}
                    aria-describedby="cr-montant-aide" />
                </div>
              </div>
              {formCheck.date && <p className="cr-erreur">{formCheck.date}</p>}
              {formCheck.futur && <p className="cr-aide">Attention : cette date est dans le futur.</p>}
              <p id="cr-montant-aide" className={formCheck.montant && modal.form.montant.trim() !== '' ? 'cr-erreur' : 'cr-aide'}>
                {modal.form.montant.trim() === ''
                  ? 'Espaces permis ; virgule ou point pour les centimes.'
                  : formCheck.montantNormalise
                    ? <>Sera enregistré : <strong>{formatMontant(Number(formCheck.montantNormalise))}</strong></>
                    : formCheck.montant}
              </p>

              {modal.nature === 'creance' && (
                <div className="form-group">
                  <label htmlFor="cr-observation">Observation</label>
                  <textarea
                    id="cr-observation"
                    rows={3}
                    placeholder="ex. Facture 2026-123, maintenance annuelle SCF"
                    value={modal.form.observation}
                    onChange={(event) => updateForm({ observation: event.target.value })}
                  />
                  <p className={formCheck.observation ? 'cr-erreur' : 'cr-aide'}>
                    {modal.form.observation.length.toLocaleString('fr-FR')} / {OBSERVATION_MAX.toLocaleString('fr-FR')} caractères
                  </p>
                </div>
              )}

              {impact && (
                <div className={`cr-impact ${impact.apres !== null && sensSolde(impact.apres) === 'crediteur' ? 'cr-impact-alerte' : ''}`}
                  role="status">
                  <div>
                    {modal.mode === 'edit' ? 'Solde du client sans cette opération' : 'Solde actuel du client'} :{' '}
                    <strong>{formatMontant(impact.avant)}</strong>
                  </div>
                  {impact.apres !== null && (
                    <div>
                      Après enregistrement : <strong>{formatMontant(impact.apres)}</strong>
                      {sensSolde(impact.apres) === 'crediteur' && ' — le client sera créditeur (avance)'}
                      {sensSolde(impact.apres) === 'solde' && ' — compte soldé'}
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={!formCheck.valid || saving}>
                  {saving
                    ? 'Enregistrement…'
                    : modal.mode === 'create'
                      ? modal.nature === 'creance' ? 'Constater la créance' : 'Enregistrer le règlement'
                      : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Creances;
